import { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import TransactionDialog from '../components/TransactionDialog';
import VoiceInput from '../components/VoiceInput';
import ReceiptScanButton from '../components/ReceiptScan';
import PeriodStepper from '../components/home/PeriodStepper';
import HomeSummary from '../components/home/HomeSummary';
import PushBanner from '../components/home/PushBanner';
import TopCategories from '../components/home/TopCategories';
import RecentMovements from '../components/home/RecentMovements';
import AlertsBell from '../components/home/AlertsBell';
import { ProfileButton } from '../components/Sidebar';
import { useTheme, cssVar } from '../context/ThemeContext';
import Joyride, { STATUS } from 'react-joyride';
import { updateProfile, connectGoogle, requestGmailAccess } from '../lib/api';
import { redirectToGoogleConsent } from '../components/GoogleConsentNotice';
import { friendlyError } from '../lib/errorReporting';

export default function Dashboard({ dashboard, dataLoading = false, categories, goals = [], tourBlocked, onRefresh, profile, onProfileUpdate, dateRange, setDateRange, onNavigate, gmailIntegration, gmailOAuthEnabled, gmailOAuthVerified, gmailAccessRequestedAt, gmailLoaded, pushState }) {
  useTheme();  // re-render on theme switch (tour colors come from CSS tokens)
  const [tourRun, setTourRun] = useState(false);
  const [showAddTxn, setShowAddTxn] = useState(false);
  const [receiptPrefill, setReceiptPrefill] = useState(null);
  const [gmailCtaBusy, setGmailCtaBusy] = useState(false);
  // Local so it can flip immediately after a successful request, without
  // waiting on App.js's next full integrations refetch.
  const [gmailAccessRequested, setGmailAccessRequested] = useState(!!gmailAccessRequestedAt);
  useEffect(() => { setGmailAccessRequested(!!gmailAccessRequestedAt); }, [gmailAccessRequestedAt]);
  const toastShownRef = useRef(false);

  // Small, always-visible-until-connected CTA — the only way to connect Gmail
  // used to be buried in Configuración → Cuentas conectadas. Reuses Settings'
  // same connect flow (redirect to Google, land back on /settings?gmail=…).
  const handleConnectGmail = async () => {
    setGmailCtaBusy(true);
    try {
      const went = await redirectToGoogleConsent(await connectGoogle());
      if (!went) setGmailCtaBusy(false);
    } catch (e) {
      setGmailCtaBusy(false);
      toast.error(friendlyError(e, 'No se pudo iniciar la conexión con Google'));
    }
  };
  // While Google's OAuth consent screen is still in Testing mode, only
  // allow-listed test users can complete it — anyone else who's never
  // connected gets a "request access" capture instead of a broken real
  // connect. Someone who needs to *reauth* already has a working connection
  // (they got through OAuth once before), so that path always uses the real
  // flow regardless of the verified flag.
  const gmailNeedsReauth = gmailIntegration?.status === 'reauth_required';
  const gmailUseRequestFlow = !gmailIntegration && !gmailOAuthVerified;
  const handleGmailCtaClick = gmailUseRequestFlow ? async () => {
    setGmailCtaBusy(true);
    try {
      await requestGmailAccess();
      setGmailAccessRequested(true);
      toast.success('¡Listo! Te avisaremos en cuanto esté disponible.');
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo enviar la solicitud'));
    } finally {
      setGmailCtaBusy(false);
    }
  } : handleConnectGmail;
  // Wait for the integrations fetch to actually resolve before deciding to
  // render this — otherwise every user briefly sees it (default state assumes
  // "not connected") and then it vanishes moments later once the real status
  // loads. That flash-then-remove sits right above the tour's very first
  // target (balance-card) — worth avoiding on its own, and especially so
  // since it's DOM churn near where react-joyride positions itself for
  // first-time users.
  const showGmailCta = gmailLoaded && gmailOAuthEnabled &&
    (gmailNeedsReauth || (!gmailIntegration && !(gmailUseRequestFlow && gmailAccessRequested)));

  // Shared by dismissedAlerts (the inline banner) and toastedAlerts (the
  // popup) — both bucket by user + current budget period so they naturally
  // reset when a new period starts, without needing their own expiry logic.
  const getPeriodKey = useCallback(() => {
    const now = new Date();
    const period = dashboard?.budget_period;
    if (period === 'weekly') {
      // ISO week start (Monday)
      const day = now.getDay();
      const diff = (day === 0 ? -6 : 1 - day);
      const monday = new Date(now);
      monday.setDate(now.getDate() + diff);
      return monday.toISOString().slice(0, 10);
    }
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, [dashboard?.budget_period]);

  const getAlertKey = useCallback(() => {
    return `mt_dismissed_${profile?.user_id || 'guest'}_${getPeriodKey()}`;
  }, [profile?.user_id, getPeriodKey]);

  // Separate from getAlertKey (the inline banner's own dismiss state) on
  // purpose: dismissing the popup should be automatic and permanent the
  // instant it's shown once — "I've seen it, stop interrupting me" — while
  // the quieter inline banner is meant to keep sitting there as an ongoing
  // reminder until the user *explicitly* dismisses it. Same bucket/expiry
  // shape as getAlertKey, just tracking a different thing.
  const getToastedKey = useCallback(() => {
    return `mt_toasted_${profile?.user_id || 'guest'}_${getPeriodKey()}`;
  }, [profile?.user_id, getPeriodKey]);

  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    // Best-effort read — key depends on dashboard which isn't available yet, so we'll sync via effect
    return [];
  });

  // Load dismissed alerts from localStorage once dashboard+profile are available
  useEffect(() => {
    if (!dashboard || !profile) return;
    const key = getAlertKey();
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      setDismissedAlerts(stored);
    } catch { setDismissedAlerts([]); }
  }, [getAlertKey, dashboard, profile]);

  const hasTopCategories = dashboard?.categories?.some(c => c.type !== 'income' && (c.spent || 0) > 0);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

  const tourSteps = [
    ...(showGmailCta ? [{
      target: '[data-testid="dashboard-gmail-cta"]',
      title: '📧 Detecta gastos por Gmail',
      content: gmailUseRequestFlow
        ? 'Estamos terminando de activar esta función — solicita acceso aquí y te avisamos apenas esté lista.'
        : 'Conecta tu Gmail y MiPisto detecta tus compras y pagos automáticamente, sin escribir nada.',
      disableBeacon: true,
      placement: 'bottom',
    }] : []),
    {
      target: '[data-testid="balance-card"]',
      title: '💰 Lo que te queda',
      content: 'Lo que te queda del periodo (ingresos menos gastos y lo que apartaste para metas), cuánto llevas gastado y cuánto puedes gastar al día. Toca el ojo para ocultar los montos.',
      disableBeacon: !showGmailCta,
      placement: 'bottom',
    },
    ...(hasTopCategories ? [{
      target: '[data-testid="top-categories"]',
      title: '📊 ¿En qué se va tu pisto?',
      content: 'Tus tres categorías con más gasto y cómo vas con su presupuesto. Toca una para ver todas en Categorías.',
      placement: 'top',
    }] : []),
    {
      target: '[data-testid="recent-transactions"]',
      title: '📋 Últimos movimientos',
      content: 'Lo último que anotaste o detectamos. "Ver todo" te lleva a Movimientos.',
      placement: 'top',
    },
    // Same left-to-right order as the action row: anotar, voz, recibo.
    {
      target: '[data-testid="dashboard-add-transaction-btn"]',
      title: '✏️ Anotar gasto',
      content: 'Registra un gasto o ingreso a mano.',
      placement: 'top',
    },
    {
      target: '[data-testid="dashboard-voice-input"]',
      title: '🎙️ Por voz',
      content: 'Presiona el micrófono y di algo como "gasté 50 en comida" — la IA hace el resto.',
      placement: 'top',
    },
    {
      target: '[data-testid="receipt-scan-btn"]',
      title: '📷 Escanea un recibo',
      content: 'Toma una foto de tu recibo: leemos el total, el comercio y la fecha, y tú solo confirmas antes de guardar.',
      placement: 'top',
    },
    // Desktop shows the sidebar, phones the bottom bar (App.css switches at
    // 768px) — point at whichever is actually on screen.
    isMobile ? {
      target: '[data-testid="mobile-nav"]',
      title: '🗺️ Navegación',
      content: 'Abajo tienes Inicio, Movimientos, Coach, Análisis y Metas. Configuración e Inversiones están en tu foto, arriba a la derecha de cada pantalla. Tus categorías están dentro de Movimientos. ¡Listo!',
      placement: 'top',
    } : {
      target: '[data-testid="sidebar"]',
      title: '🗺️ Navegación',
      content: 'Desde aquí vas a Movimientos, Coach, Análisis, Metas, Inversiones y Configuración. Tus categorías están dentro de Movimientos, con el botón "Categorías". ¡Listo!',
      placement: 'right',
    },
  ];

  const handleJoyrideCallback = useCallback(async (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setTourRun(false);
      try {
        const updated = await updateProfile({ has_seen_tour: true });
        if (onProfileUpdate) onProfileUpdate(updated);
      } catch (e) {
        console.error('Error marking tour as seen:', e);
      }
    }
  }, [onProfileUpdate]);

  // Auto-start tour for first-time users once dashboard data is loaded
  useEffect(() => {
    // tourBlocked: a shared-goal invite prompt is up — don't stack the tour on it.
    if (profile && !profile.has_seen_tour && dashboard && !tourBlocked) {
      const t = setTimeout(() => setTourRun(true), 800);
      return () => clearTimeout(t);
    }
  }, [profile, dashboard, tourBlocked]);

  // Show toast notifications for critical/exceeded alerts — but only once
  // per alert, ever (well, per budget period — see getToastedKey). Used to
  // gate solely on toastShownRef, a plain useRef that resets on every fresh
  // mount of this component, so the *exact same* still-active alert popped
  // up again every single time the dashboard was revisited or the app was
  // reopened, even seconds after it was last seen. Now checked/persisted to
  // localStorage instead, so once shown it's genuinely done for the period.
  useEffect(() => {
    if (dashboard?.alerts && dashboard.alerts.length > 0 && !toastShownRef.current) {
      toastShownRef.current = true;
      const key = getToastedKey();
      let toasted = [];
      try { toasted = JSON.parse(localStorage.getItem(key) || '[]'); } catch { toasted = []; }
      const toShow = dashboard.alerts.filter(
        a => !toasted.includes(a.category_id) && !dismissedAlerts.includes(a.category_id)
      );
      if (toShow.length === 0) return;
      toShow.forEach((alert, i) => {
        setTimeout(() => {
          if (alert.level === 'exceeded') {
            toast.error(alert.message, { duration: 6000 });
          } else if (alert.level === 'critical') {
            toast.warning(alert.message, { duration: 5000 });
          }
        }, i * 800);
      });
      try {
        localStorage.setItem(key, JSON.stringify([...toasted, ...toShow.map(a => a.category_id)]));
      } catch { /* best effort — worst case it just re-toasts next visit */ }
    }
  }, [dashboard?.alerts, dismissedAlerts, getToastedKey]);

  const loading = !dashboard || dataLoading;
  // Joyride takes literal colors, so read the current theme's tokens.
  const tour = { card: cssVar('--card'), text: cssVar('--text'), muted: cssVar('--text-2'), brand: cssVar('--brand') };
  const firstName = (profile?.name || '').trim().split(' ')[0];
  const openCategories = () => onNavigate && onNavigate('categories');
  const openTransactions = () => onNavigate && onNavigate('transactions');

  // Inicio (redesign.md, Fase 4), top to bottom: greeting + period + bell +
  // avatar; the brand block (what's left, per-day guide); the three biggest
  // categories; latest movements; and the "Anotar gasto" row, pinned above the
  // tab bar on phones (thumb zone) and inline at the end on desktop.
  return (
    <div className="space-y-8 pb-24 md:pb-0" data-testid="dashboard-page">
      {dashboard && (
        <Joyride
          steps={tourSteps}
          run={tourRun}
          continuous
          showSkipButton
          showProgress
          callback={handleJoyrideCallback}
          locale={{
            back: 'Atrás',
            close: 'Cerrar',
            last: '¡Listo!',
            next: 'Siguiente',
            // showProgress uses this one instead of `next` (default is English).
            nextLabelWithProgress: 'Siguiente ({step} de {steps})',
            skip: 'Saltar tour',
          }}
          styles={{
            options: {
              arrowColor: tour.card,
              backgroundColor: tour.card,
              overlayColor: 'rgba(0, 0, 0, 0.65)',
              primaryColor: tour.brand,
              textColor: tour.text,
              zIndex: 10000,
            },
            tooltip: {
              borderRadius: '16px',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
            },
            tooltipTitle: {
              fontSize: '15px',
              fontWeight: '600',
              color: tour.text,
            },
            buttonNext: {
              borderRadius: '10px',
              padding: '8px 18px',
              fontWeight: '500',
            },
            buttonBack: {
              color: tour.muted,
            },
            buttonSkip: {
              color: tour.muted,
            },
          }}
        />
      )}

      {/* !mt-0: Joyride renders before this, and space-y-8 would push the
          title lower than on every other page. */}
      <header className="!mt-0">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white min-w-0 truncate">
            Hola{firstName ? `, ${firstName}` : ''} 👋
          </h1>
          <div className="flex items-center shrink-0 -mr-1.5">
            <AlertsBell dashboard={dashboard} onOpenTransactions={openTransactions} onOpenCategories={openCategories} />
            <ProfileButton />
          </div>
        </div>
        <PeriodStepper budgetPeriod={profile?.budget_period || dashboard?.budget_period} dateRange={dateRange} setDateRange={setDateRange} />
      </header>

      {/* Gmail connect CTA — one quiet line, only shown until connected (or
          if the connection needs reconnecting). Configuración still owns the
          full account-management UI. */}
      {showGmailCta && (
        <button
          type="button"
          onClick={handleGmailCtaClick}
          disabled={gmailCtaBusy}
          data-testid="dashboard-gmail-cta"
          className="w-full flex items-center gap-2.5 px-3.5 py-3 rounded-xl text-sm text-left transition-colors disabled:opacity-50 bg-white/[0.03] hover:bg-white/[0.06] active:scale-[0.99]"
        >
          <Mail className="w-4 h-4 shrink-0 text-violet-400" strokeWidth={1.75} />
          <span className="flex-1 min-w-0 truncate text-slate-300">
            {gmailNeedsReauth
              ? 'Tu conexión con Gmail venció — reconéctala para seguir detectando gastos solo.'
              : gmailUseRequestFlow
                ? 'Estamos activando la detección automática por Gmail — solicita acceso.'
                : 'Conecta tu Gmail y MiPisto detecta tus gastos automáticamente.'}
          </span>
          <span className="shrink-0 text-xs font-semibold text-violet-400">
            {gmailNeedsReauth ? 'Reconectar' : gmailUseRequestFlow ? 'Solicitar' : 'Conectar'}
          </span>
        </button>
      )}

      <HomeSummary dashboard={dashboard} loading={loading} />

      <PushBanner pushState={pushState} onEnabled={onProfileUpdate} />

      <TopCategories categories={dashboard?.categories || []} loading={loading} onOpenCategories={openCategories} />

      <RecentMovements
        transactions={dashboard?.recent_transactions || []}
        loading={loading}
        onSeeAll={onNavigate ? openTransactions : undefined}
        onAddTransaction={() => setShowAddTxn(true)}
      />

      <div
        className="fixed inset-x-0 bottom-[calc(var(--nav-h)+env(safe-area-inset-bottom))] z-40 px-4 pt-3 pb-3 bg-surface/95 border-t border-white/5 md:static md:z-auto md:p-0 md:bg-transparent md:border-0"
        data-testid="dashboard-actions"
      >
        <div className="max-w-5xl mx-auto flex items-center gap-2.5">
          <Button
            data-testid="dashboard-add-transaction-btn"
            onClick={() => setShowAddTxn(true)}
            className="flex-1 h-14 rounded-2xl bg-brand hover:bg-brand-hover text-on-brand text-base font-semibold active:scale-[0.98] transition-colors"
          >
            <Plus className="w-5 h-5 mr-1.5" strokeWidth={2.25} /> Anotar gasto
          </Button>
          <VoiceInput categories={categories} onSuccess={onRefresh} containerTestId="dashboard-voice-input" square />
          <ReceiptScanButton variant="square" onParsed={(values) => { setReceiptPrefill(values); setShowAddTxn(true); }} />
        </div>
      </div>

      <TransactionDialog
        open={showAddTxn}
        onOpenChange={(o) => { setShowAddTxn(o); if (!o) setReceiptPrefill(null); }}
        categories={categories}
        onSuccess={onRefresh}
        goals={goals}
        prefill={receiptPrefill}
      />
    </div>
  );
}
